import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clienteAuthService from '../api/authService';
import logo from '../assets/logo.png';

const NAV_LINKS = [
  { to: '/catalogo', label: 'Catalogo' },
  { to: '/diventa-closer', label: '🎁 Guadagna con noi' },
  { to: '/diventa-partner', label: 'Vendi su Mangitalia' },
  { to: '/chi-siamo', label: 'Chi siamo' },
  { to: '/contatti', label: 'Contattaci' },
];

/**
 * Navbar con styling condizionale in base alla rotta.
 * - Sulle pagine con hero scura a tutto schermo (Home, Diventa Closer),
 *   la navbar parte trasparente con testo bianco.
 * - Sulle altre pagine (Catalogo, Partner, ecc) parte subito con
 *   lo sfondo solido e testo colorato standard del brand.
 */
export default function Navbar() {
  const location = useLocation();
  const navRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuth, setIsAuth] = useState(clienteAuthService.isAuthenticated?.() || false);

  // ─── Scroll listener ─────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ─── Chiudi menu mobile al cambio rotta ──────────────────────────────────
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // ─── Auth state al cambio rotta ──────────────────────────────────────────
  useEffect(() => {
    setIsAuth(clienteAuthService.isAuthenticated?.() || false);
  }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Determina se la pagina corrente ha una hero a tutto schermo (che va dietro la navbar)
  const hasDarkHero = ['/', '/diventa-closer'].includes(location.pathname);

  // ─── Calcola stato visivo ─────────────────────────────────────────────────
  // transparent = su pagina con hero, non scrollata e menu chiuso
  const transparent = hasDarkHero && !scrolled && !menuOpen;
  
  // isDark = testo bianco (usato quando la navbar è trasparente sulla hero scura)
  const isDark = transparent;

  // Colori testo adattivi
  const logoFilter = isDark ? 'brightness(0) invert(1)' : undefined;

  return (
    <>
      <nav
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-[900] transition-all duration-300"
        style={{
          background: transparent ? 'transparent' : 'rgba(255,253,250,0.97)',
          backdropFilter: transparent ? 'none' : 'blur(12px)',
          WebkitBackdropFilter: transparent ? 'none' : 'blur(12px)',
          borderBottom: transparent ? 'none' : '1px solid #f3deb8',
          boxShadow: transparent ? 'none' : '0 1px 8px rgba(199,130,68,0.06)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-14">

          {/* ── Logo ──────────────────────────────────────────────────────── */}
          <Link to="/" aria-label="Mangitalia Home" className="flex items-center gap-2 shrink-0">
            <img
              src={logo}
              alt="Mangitalia"
              className="h-8 w-auto transition-all duration-300"
              style={{ filter: logoFilter }}
            />
            <span
              className="font-sacramento text-xl hidden sm:block transition-colors duration-300"
              style={{ color: scrolled || menuOpen ? '#c78244' : isDark ? 'white' : '#c78244' }}
            >
              Mangitalia
            </span>
          </Link>

          {/* ── Desktop nav ───────────────────────────────────────────────── */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-300"
                  style={{
                    color: scrolled
                      ? active ? '#c78244' : '#374151'
                      : isDark
                        ? 'rgba(255,255,255,0.95)'
                        : active ? '#c78244' : '#374151',
                    background: scrolled && active
                      ? 'rgba(199,130,68,0.1)'
                      : !scrolled && isDark && active
                        ? 'rgba(255,255,255,0.15)'
                        : 'transparent',
                    textShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* ── Right: Auth CTA + Hamburger ───────────────────────────────── */}
          <div className="flex items-center gap-2">
            <Link
              to={isAuth ? '/area-riservata' : '/area-riservata/login'}
              className="hidden sm:flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300"
              style={
                scrolled || menuOpen
                  ? { background: '#c78244', color: 'white' }
                  : isDark
                    ? {
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1.5px solid rgba(255,255,255,0.6)',
                        backdropFilter: 'blur(4px)',
                        textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }
                    : { background: '#c78244', color: 'white' }
              }
            >
              {isAuth ? '👤 Area riservata' : 'Accedi'}
            </Link>

            {/* Hamburger */}
            <button
              type="button"
              aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'}
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden flex flex-col justify-center gap-[5px] p-2 rounded-lg"
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="block h-[2px] w-5 rounded transition-all duration-200"
                  style={{
                    background: scrolled || menuOpen
                      ? '#c78244'
                      : isDark
                        ? 'white'
                        : '#c78244',
                    transform:
                      menuOpen && i === 0
                        ? 'rotate(45deg) translate(5px, 5px)'
                        : menuOpen && i === 2
                          ? 'rotate(-45deg) translate(5px, -5px)'
                          : 'none',
                    opacity: menuOpen && i === 1 ? 0 : 1,
                  }}
                />
              ))}
            </button>
          </div>
        </div>

        {/* ── Mobile menu ─────────────────────────────────────────────────── */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22 }}
              className="md:hidden overflow-hidden border-t border-[#f3deb8] bg-[#FFFEFA]"
            >
              <div className="px-4 py-3 space-y-1">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive(link.to)
                        ? 'bg-[#c78244]/10 text-[#c78244]'
                        : 'text-gray-700 hover:text-[#c78244] hover:bg-[#c78244]/5'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-2 border-t border-[#f3deb8] mt-2">
                  <Link
                    to={isAuth ? '/area-riservata' : '/area-riservata/login'}
                    className="block w-full text-center bg-[#c78244] text-white px-4 py-2.5 rounded-xl text-sm font-semibold"
                  >
                    {isAuth ? '👤 Area riservata' : 'Accedi'}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Spacer */}
      <div className="h-14" aria-hidden="true" />
    </>
  );
}
